import { components as antdComponents } from '@yimoka/antd';
import { validateRuntimeDocument, type RuntimeJsonValue } from '@yimoka/designable-playground/runtime';
import { components as reactComponents } from '@yimoka/react';

import type {
  IRealmAssetManifestItem,
  IRealmCurrentReply,
  IRealmProfileSnapshot,
  IRealmWebProfile,
} from './types';

const MAX_PROFILE_BYTES = 256 * 1024;
const MAX_SCHEMA_DEPTH = 12;
const MAX_SCHEMA_NODES = 200;
const RUNTIME_COMPONENT_NAMES = new Set([
  ...Object.keys(reactComponents),
  ...Object.keys(antdComponents),
]);
const ALLOWED_PROFILE_KEYS = new Set(['kind', 'version', 'brand', 'theme', 'documentMeta', 'homepage', 'assetManifest']);

/** isRecord 判断 JSON 值是否为非数组对象。 */
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** hasOnlyKeys 拒绝 Profile 合同之外的扩展字段，避免未知值进入 DOM 与主题映射。 */
function hasOnlyKeys(value: Record<string, unknown>, allowed: readonly string[]): boolean {
  const allowedSet = new Set(allowed);
  return Object.keys(value).every(key => allowedSet.has(key));
}

/** isStringRecord 校验多语言对象只包含非空 locale 和字符串值。 */
function isStringRecord(value: unknown): value is Record<string, string> {
  return isRecord(value) && Object.entries(value).every(([key, item]) => key.trim().length > 0 && typeof item === 'string');
}

/** isSafeNavigationURL 拒绝脚本、data 与协议相对 URL，只开放站内路径、锚点和 HTTPS。 */
export function isSafeNavigationURL(value: unknown): value is string {
  if (typeof value !== 'string' || value.length > 2048) return false;
  if (value.startsWith('#') || value.startsWith('/')) return !value.startsWith('//');
  try {
    return new URL(value).protocol === 'https:';
  } catch {
    return false;
  }
}

/** isSafeAssetSource 允许站内素材或 HTTPS 对象存储/CDN，并拒绝可执行协议与带凭据地址。 */
function isSafeAssetSource(value: unknown): value is string {
  if (typeof value !== 'string' || value.length > 2048) return false;
  if (value.startsWith('/assets/')) return !value.includes('..');
  try {
    const url = new URL(value);
    // 渠道物料不随 tenant-web 发版；仅允许浏览器可安全加载的 HTTPS 云端地址。
    return url.protocol === 'https:' && url.username === '' && url.password === '';
  } catch {
    return false;
  }
}

/** validateComponentPropValue 递归拒绝事件、HTML 和可发起外域请求的样式 URL。 */
function validateComponentPropValue(key: string, value: unknown): boolean {
  if (/^on[A-Z]/.test(key) || key === 'dangerouslySetInnerHTML' || key === 'html') return false;
  if (['href', 'to'].includes(key)) return isSafeNavigationURL(value);
  if (key === 'src') return isSafeAssetSource(value);
  if (key === 'style') {
    if (!isRecord(value)) return false;
    return Object.values(value).every(item => typeof item !== 'string' || !/url\s*\(/i.test(item));
  }
  if (Array.isArray(value)) {
    return value.every(item => (isRecord(item)
      ? Object.entries(item).every(([childKey, childValue]) => validateComponentPropValue(childKey, childValue))
      : ['boolean', 'number', 'string'].includes(typeof item) || item === null));
  }
  if (isRecord(value)) {
    return Object.entries(value).every(([childKey, childValue]) => validateComponentPropValue(childKey, childValue));
  }
  return ['boolean', 'number', 'string'].includes(typeof value) || value === null;
}

/** validateAsset 验证素材完整性、权利和尺寸，品牌引用会在 Profile 层继续求交。 */
function validateAsset(value: unknown): value is IRealmAssetManifestItem {
  if (!isRecord(value)) return false;
  return hasOnlyKeys(value, ['key', 'src', 'source', 'usageRight', 'alt', 'width', 'height', 'sha256'])
    && typeof value.key === 'string'
    && value.key.length > 0
    && isSafeAssetSource(value.src)
    && (value.source === 'partner' || value.source === 'self-made')
    && (value.usageRight === 'authorized' || value.usageRight === 'owned')
    && typeof value.alt === 'string'
    && value.alt.length > 0
    && Number.isInteger(value.width)
    && Number(value.width) > 0
    && Number(value.width) <= 8192
    && Number.isInteger(value.height)
    && Number(value.height) > 0
    && Number(value.height) <= 8192
    && typeof value.sha256 === 'string'
    && /^[0-9a-f]{64}$/i.test(value.sha256);
}

/** validateSchemaNode 递归校验组件已由 Yimoka 基座注册，并保留深度与 URL 安全边界。 */
function validateSchemaNode(value: unknown, depth = 0, state = { count: 0 }): boolean {
  if (!isRecord(value) || depth > MAX_SCHEMA_DEPTH) return false;
  state.count += 1;
  if (state.count > MAX_SCHEMA_NODES) return false;
  if (value['x-component'] !== undefined && (
    typeof value['x-component'] !== 'string' || !RUNTIME_COMPONENT_NAMES.has(value['x-component'])
  )) return false;
  const componentProps = value['x-component-props'];
  if (componentProps !== undefined) {
    if (!isRecord(componentProps)) return false;
    if (!Object.entries(componentProps).every(([key, item]) => validateComponentPropValue(key, item))) return false;
  }
  for (const key of ['properties', 'items']) {
    const child = value[key];
    if (child === undefined) continue;
    if (key === 'properties') {
      if (!isRecord(child) || !Object.values(child).every(item => validateSchemaNode(item, depth + 1, state))) return false;
    } else if (Array.isArray(child)) {
      if (!child.every(item => validateSchemaNode(item, depth + 1, state))) return false;
    } else if (!validateSchemaNode(child, depth + 1, state)) return false;
  }
  return true;
}

// 完整 Profile 是原子发布边界，集中校验顶层、素材、品牌引用和 Runtime 文档便于拒绝半合法快照。
/** validateRealmWebProfile 校验原子品牌快照、组件语义和受控云端素材。 */
export function validateRealmWebProfile(value: unknown): value is IRealmWebProfile { // eslint-disable-line complexity
  if (!isRecord(value)) return false;
  let encoded = '';
  try {
    encoded = JSON.stringify(value as RuntimeJsonValue);
  } catch {
    return false;
  }
  if (new TextEncoder().encode(encoded).byteLength > MAX_PROFILE_BYTES) return false;
  if (Object.keys(value).some(key => !ALLOWED_PROFILE_KEYS.has(key))) return false;
  if (value.kind !== 'realm-web-profile' || value.version !== 1) return false;
  if (!isRecord(value.brand) || !isRecord(value.theme) || !isRecord(value.documentMeta)) return false;
  if (!hasOnlyKeys(value.brand, ['shortTitle', 'logoAssetKey', 'logoDarkAssetKey', 'faviconAssetKey'])
    || !Object.values(value.brand).every(item => typeof item === 'string')) return false;
  if (!hasOnlyKeys(value.theme, ['primaryColor', 'infoColor', 'successColor', 'warningColor', 'errorColor', 'borderRadius', 'fontFamilyToken', 'headerMode'])) return false;
  for (const key of ['primaryColor', 'infoColor', 'successColor', 'warningColor', 'errorColor']) {
    const color = value.theme[key];
    if (color !== undefined && (typeof color !== 'string' || !/^#[0-9a-f]{6}$/i.test(color))) return false;
  }
  if (value.theme.borderRadius !== undefined && (
    !Number.isInteger(value.theme.borderRadius) || Number(value.theme.borderRadius) < 0 || Number(value.theme.borderRadius) > 32
  )) return false;
  if (value.theme.fontFamilyToken !== undefined && value.theme.fontFamilyToken !== 'system') return false;
  if (value.theme.headerMode !== undefined && !['brand', 'dark', 'light'].includes(String(value.theme.headerMode))) return false;
  if (!hasOnlyKeys(value.documentMeta, ['titleTemplate', 'description', 'descriptionI18n', 'ogImageAssetKey'])) return false;
  for (const key of ['titleTemplate', 'description', 'ogImageAssetKey']) {
    const item = value.documentMeta[key];
    if (item !== undefined && typeof item !== 'string') return false;
  }
  if (value.documentMeta.descriptionI18n !== undefined && !isStringRecord(value.documentMeta.descriptionI18n)) return false;
  if (!Array.isArray(value.assetManifest) || value.assetManifest.length > 64 || !value.assetManifest.every(validateAsset)) return false;
  const assetKeys = new Set(value.assetManifest.map(asset => asset.key));
  if (assetKeys.size !== value.assetManifest.length) return false;
  for (const key of ['logoAssetKey', 'logoDarkAssetKey', 'faviconAssetKey'] as const) {
    const reference = value.brand[key];
    if (reference !== undefined && (typeof reference !== 'string' || !assetKeys.has(reference))) return false;
  }
  const ogImageKey = value.documentMeta.ogImageAssetKey;
  if (ogImageKey !== undefined && (typeof ogImageKey !== 'string' || (ogImageKey !== '' && !assetKeys.has(ogImageKey)))) return false;
  const { homepage } = value;
  if (!isRecord(homepage)
    || homepage.kind !== 'yimoka.designable.runtime-document'
    || homepage.version !== 1
    || !validateRuntimeDocument(homepage).valid
    || !validateSchemaNode(homepage.schema)) return false;
  return true;
}

/** validateRealmProfileSnapshot 校验 resolver/preview 的完整原子快照。 */
export function validateRealmProfileSnapshot(value: unknown): value is IRealmProfileSnapshot {
  if (!isRecord(value)) return false;
  return typeof value.realmName === 'string' && value.realmName.trim().length > 0
    && typeof value.publishedRevision === 'string' && /^\d+$/.test(value.publishedRevision)
    && typeof value.contentHash === 'string' && /^[0-9a-f]{64}$/i.test(value.contentHash)
    && typeof value.title === 'string' && value.title.trim().length > 0
    && isStringRecord(value.titleI18n)
    && validateRealmWebProfile(value.webProfile);
}

/** parseRealmCurrentReply 把 IAM 直接返回的当前版本投影收敛为 tenant-web 的原子渲染快照。 */
export function parseRealmCurrentReply(
  value: unknown,
  expectedRealm: string,
  locale: string,
): IRealmProfileSnapshot | undefined {
  if (!isRecord(value)) return undefined;
  const reply = value as IRealmCurrentReply;
  const { revision } = reply;
  let publishedRevision = '';
  if (typeof revision === 'string') publishedRevision = revision;
  else if (Number.isSafeInteger(revision) && Number(revision) >= 0) publishedRevision = String(revision);
  const title = typeof reply.titleLocal === 'string' && reply.titleLocal.trim().length > 0
    ? reply.titleLocal
    : reply.title;
  const snapshot: IRealmProfileSnapshot = {
    contentHash: typeof reply.contentHash === 'string' ? reply.contentHash : '',
    publishedRevision,
    realmName: expectedRealm,
    title: typeof title === 'string' ? title : '',
    // 通用 Portal 投影按 Accept-Language 返回 titleLocal；Runtime 只需保存当前语言的稳定映射。
    titleI18n: typeof title === 'string' ? { [locale]: title } : {},
    webProfile: reply.webProfile as IRealmWebProfile,
  };
  return validateRealmProfileSnapshot(snapshot) ? snapshot : undefined;
}
